import React, { useMemo } from "react";
import { AgGrid, Flex, Text, HelpIndicator, Link } from "@jsq/ds/core";
import { URL_PARAMS, WORKFLOWS_ROUTE } from "../../constants";
import {
  buildIndividualUrlFilters,
  IndividualDueDateRangeKey,
  IndividualFilterData,
} from "../../filterUtils";
import * as styles from "./styles_vanilla.css";
import { IndividualRowItem } from "./types";

export { WorkflowTypeChips } from "../../WorkflowTypeChips";

type IndividualCellRendererParams = AgGrid.ICellRendererParams<IndividualRowItem>;

type ClickableCellParams = IndividualCellRendererParams & {
  dueDateRangeKey?: IndividualDueDateRangeKey;
  isActiveWorkflows?: boolean;
  useWorkflowEngine?: boolean;
};

const getRiskColorClass = (value: number): string => {
  if (value <= 1) return styles.risk01;
  if (value <= 3) return styles.risk23;
  if (value <= 5) return styles.risk45;
  if (value <= 7) return styles.risk67;
  if (value <= 9) return styles.risk89;
  return styles.risk10Plus;
};

/**
 * @deprecated Use buildIndividualUrlFilters from filterUtils, which handles both
 * the legacy Jira grid and WorkflowEngine filter key/value formats.
 *
 * This wrapper retains the legacy (crltWorkflowEngine=OFF) behaviour so that
 * existing callers (columnDefs.ts, tests) are not broken until fully migrated.
 */
export const buildFilterModel = (
  data: IndividualFilterData,
  dueDateRangeKey?: IndividualDueDateRangeKey,
): AgGrid.FilterModel => buildIndividualUrlFilters(data, false, dueDateRangeKey);

export const ClickableCountCell: React.FC<ClickableCellParams> = ({
  value,
  data,
  dueDateRangeKey,
  isActiveWorkflows,
  useWorkflowEngine = false,
}) => {
  const numericValue = typeof value === "number" ? value : 0;

  const linkTo = useMemo(() => {
    if (!data) return "";
    const filters = buildIndividualUrlFilters(
      data,
      useWorkflowEngine,
      isActiveWorkflows ? undefined : dueDateRangeKey,
    );
    const params = new URLSearchParams();
    params.set(URL_PARAMS.FILTERS_PARAM, JSON.stringify(filters));
    return `${WORKFLOWS_ROUTE}?${params.toString()}`;
  }, [data, dueDateRangeKey, isActiveWorkflows, useWorkflowEngine]);

  if (numericValue === 0) {
    return (
      <Flex alignItems="center" className={styles.clickableCellWrapper}>
        <Text>{numericValue}</Text>
      </Flex>
    );
  }

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      className={styles.clickableCellWrapper}
    >
      <Text>{numericValue}</Text>
      <Flex alignItems="center" className={styles.hoverIconContainer}>
        <HelpIndicator
          id={`view-tickets-tooltip-${data?.assigneeId}-${dueDateRangeKey}`}
          content="View tickets"
        >
          <Link
            to={linkTo}
            icon="external-link"
            iconSize="medium"
            iconVariant="outline"
            className={styles.hoverIcon}
            data-testid="view-tickets-icon"
          >
            {""}
          </Link>
        </HelpIndicator>
      </Flex>
    </Flex>
  );
};

export const RiskCellRenderer: React.FC<ClickableCellParams> = (props) => {
  const { value } = props;
  const numericValue = typeof value === "number" ? value : 0;
  const riskClass = getRiskColorClass(numericValue);

  return (
    <Flex alignItems="center" className={`${styles.cellNumber} ${riskClass}`}>
      <ClickableCountCell {...props} />
    </Flex>
  );
};

export const UserCellRenderer: React.FC<IndividualCellRendererParams> = ({ data }) => {
  if (!data) return null;

  return (
    <Flex flexDirection="column" gap={2} className={styles.userCellContent}>
      <Text className={styles.userName}>{data.assigneeName}</Text>
      <Text className={styles.userEmail}>{data.assigneeEmail}</Text>
    </Flex>
  );
};

export const NumericCellRenderer: React.FC<ClickableCellParams> = (props) => (
  <Flex flexDirection="column" gap={2} className={styles.numericCellContent}>
    <ClickableCountCell {...props} />
  </Flex>
);
